import React, { Component } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';

class Jobs extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      jobs: [],
      loading: true,
      activeTab: 'active', // 'active' or 'archived'
      searchQuery: '',
      sortBy: 'created_at',
      sortOrder: 'DESC',
      
      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      
      // Modals
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showShareModal: false,
      
      // Forms
      newJobName: '',
      editJob: null,
      editJobName: '',
      selectedJob: null,
      
      // Share data
      shareableUsers: [],
      currentShares: [],
      loadingShares: false,
      
      // UI state
      errors: {},
      actionLoading: false,

      // Add subscription tracking
      jobSubscriptions: {}, // jobId -> subscription object
      loadingSubscriptions: {},
    };
  }

  async componentDidMount() {
    await this.fetchJobs();
  }

  fetchJobs = async () => {
    this.setState({ loading: true });
  
    try {
      const { currentPage, searchQuery, sortBy, sortOrder, activeTab } = this.state;
  
      // Convert activeTab to archived boolean
      const archived = activeTab === 'archived';
  
      const response = await api.get('/jobs', {
        params: {
          page: currentPage,
          limit: 20,
          search: searchQuery,
          sortBy,
          sortOrder,
          archived,
        },
      });
  
      const jobs = response.data.jobs;
      
      // Build subscription map from job data (no extra queries!)
      const subscriptionMap = {};
      jobs.forEach(job => {
        if (job.subscription) {
          subscriptionMap[job.id] = {
            subscribed: true,
            subscription: job.subscription,
          };
        } else {
          subscriptionMap[job.id] = {
            subscribed: false,
          };
        }
      });
      
      this.setState({
        jobs,
        totalPages: response.data.pagination.totalPages,
        totalCount: response.data.pagination.total,
        jobSubscriptions: subscriptionMap, // ✅ Set from job data
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      this.setState({ loading: false });
    }
  };

  handleTabChange = async (tab) => {
    this.setState({ 
      activeTab: tab, 
      currentPage: 1,
      searchQuery: '',
      loading: true 
    }, () => {
      this.fetchJobs();
    });
  };

  handleSortChange = (newSortBy) => {
    const { sortBy, sortOrder } = this.state;
    
    // If clicking same column, toggle order
    if (sortBy === newSortBy) {
      this.setState({ 
        sortOrder: sortOrder === 'DESC' ? 'ASC' : 'DESC',
        loading: true 
      }, () => {
        this.fetchJobs();
      });
    } else {
      // New column, default to DESC
      this.setState({ 
        sortBy: newSortBy, 
        sortOrder: 'DESC',
        loading: true 
      }, () => {
        this.fetchJobs();
      });
    }
  };

  handleSearch = (e) => {
    const searchQuery = e.target.value;
    this.setState({ searchQuery, currentPage: 1 }, () => {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.setState({ loading: true }, () => {
          this.fetchJobs();
        });
      }, 500);
    });
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page, loading: true }, () => {
      this.fetchJobs();
    });
  };

  canModifyJob = (job) => {
    const { user } = this.context;
    return job.isOwner || user?.admin;
  };
  
  // ✅ Add this method
  canShareJob = (job) => {
    // Both owners AND admins can share
    const { user } = this.context;
    return job.isOwner || user?.admin;
  };

  // Add Job
  openAddModal = () => {
    this.setState({ showAddModal: true, newJobName: '', errors: {} });
  };

  closeAddModal = () => {
    this.setState({ showAddModal: false, newJobName: '', errors: {} });
  };

  handleAddJob = async (e) => {
    e.preventDefault();
    const { newJobName } = this.state;

    if (!newJobName.trim()) {
      this.setState({ errors: { newJobName: 'Job name is required' } });
      return;
    }

    this.setState({ actionLoading: true });

    try {
      await api.post('/jobs', { name: newJobName });
      await this.fetchJobs();
      this.closeAddModal();
      this.setState({ actionLoading: false });
    } catch (error) {
      this.setState({
        errors: { general: error.response?.data?.message || 'Failed to create job' },
        actionLoading: false,
      });
    }
  };

  // Edit Job
  openEditModal = (job) => {
    this.setState({
      showEditModal: true,
      editJob: job,
      editJobName: job.name,
      errors: {},
    });
  };

  closeEditModal = () => {
    this.setState({
      showEditModal: false,
      editJob: null,
      editJobName: '',
      errors: {},
    });
  };

  handleEditJob = async (e) => {
    e.preventDefault();
    const { editJob, editJobName } = this.state;

    if (!editJobName.trim()) {
      this.setState({ errors: { editJobName: 'Job name is required' } });
      return;
    }

    this.setState({ actionLoading: true });

    try {
      await api.put(`/jobs/${editJob.id}`, { name: editJobName });
      await this.fetchJobs();
      this.closeEditModal();
      this.setState({ actionLoading: false });
    } catch (error) {
      this.setState({
        errors: { general: error.response?.data?.message || 'Failed to update job' },
        actionLoading: false,
      });
    }
  };

  // Archive/Restore Job
  handleToggleArchive = async (job) => {
    const action = job.archived ? 'restore' : 'archive';
    if (window.confirm(`Are you sure you want to ${action} "${job.name}"?`)) {
      try {
        await api.patch(`/jobs/${job.id}/archive`);
        await this.fetchJobs();
      } catch (error) {
        alert(error.response?.data?.message || `Failed to ${action} job`);
      }
    }
  };

  // Delete Job
  openDeleteModal = (job) => {
    this.setState({ showDeleteModal: true, selectedJob: job });
  };

  closeDeleteModal = () => {
    this.setState({ showDeleteModal: false, selectedJob: null });
  };

  handleDeleteJob = async () => {
    const { selectedJob } = this.state;
    this.setState({ actionLoading: true });
  
    try {
      await api.delete(`/jobs/${selectedJob.id}`);
      await this.fetchJobs();
      this.closeDeleteModal();
      this.closeEditModal();
      this.setState({ actionLoading: false });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete job');
      this.setState({ actionLoading: false });
    }
  };

  // Share Job
  openShareModal = async (job) => {
    this.setState({ 
      showShareModal: true, 
      selectedJob: job,
      loadingShares: true,
      errors: {},
    });

    try {
      const [sharesResponse, usersResponse] = await Promise.all([
        api.get(`/jobs/${job.id}/shares`),
        api.get(`/jobs/${job.id}/shareable-users`)
      ]);

      this.setState({
        currentShares: sharesResponse.data.shares,
        shareableUsers: usersResponse.data.users,
        loadingShares: false,
      });
    } catch (error) {
      this.setState({
        errors: { share: error.response?.data?.message || 'Failed to load share data' },
        loadingShares: false,
      });
    }
  };

  closeShareModal = () => {
    this.setState({
      showShareModal: false,
      selectedJob: null,
      currentShares: [],
      shareableUsers: [],
      errors: {},
    });
  };

  handleShareWithUser = async (userId) => {
    const { selectedJob } = this.state;
    
    try {
      await api.post(`/jobs/${selectedJob.id}/shares`, { userId });
      
      // Refresh share data
      const [sharesResponse, usersResponse] = await Promise.all([
        api.get(`/jobs/${selectedJob.id}/shares`),
        api.get(`/jobs/${selectedJob.id}/shareable-users`)
      ]);

      this.setState({
        currentShares: sharesResponse.data.shares,
        shareableUsers: usersResponse.data.users,
      });
    } catch (error) {
      this.setState({
        errors: { share: error.response?.data?.message || 'Failed to share job' }
      });
    }
  };

  handleUnshare = async (userId) => {
    const { selectedJob } = this.state;

    if (window.confirm('Remove access for this user?')) {
      try {
        await api.delete(`/jobs/${selectedJob.id}/shares/${userId}`);
        
        // Refresh share data
        const [sharesResponse, usersResponse] = await Promise.all([
          api.get(`/jobs/${selectedJob.id}/shares`),
          api.get(`/jobs/${selectedJob.id}/shareable-users`)
        ]);

        this.setState({
          currentShares: sharesResponse.data.shares,
          shareableUsers: usersResponse.data.users,
        });
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to unshare job');
      }
    }
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  getSortIcon = (column) => {
    const { sortBy, sortOrder } = this.state;
    
    if (sortBy !== column) {
      return (
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortOrder === 'DESC' ? (
      <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  };

  toggleWatch = async (job) => {
    const { jobSubscriptions, loadingSubscriptions } = this.state;
    const subscription = jobSubscriptions[job.id];
    const isWatching = subscription?.subscribed;
  
    // Prevent double-clicks
    if (loadingSubscriptions[job.id]) {
      return;
    }
  
    this.setState({
      loadingSubscriptions: { ...loadingSubscriptions, [job.id]: true }
    });
  
    try {
      if (isWatching) {
        // Unsubscribe
        await api.delete(`/subscriptions/job/${job.id}`);
      } else {
        // Subscribe
        await api.post(`/subscriptions/job/${job.id}`, {
          notifyOnUpdate: true,
          notifyOnShare: true,
          notifyOnDelete: true,
          notifyOnArchive: true,
        });
      }
      
      // Refresh jobs to get updated subscription status
      await this.fetchJobs();
      
      // Reset loading state after fetchJobs completes
      this.setState({
        loadingSubscriptions: { ...this.state.loadingSubscriptions, [job.id]: false }
      });
    } catch (error) {
      console.error('Failed to toggle watch:', error);
      this.setState({
        loadingSubscriptions: { ...this.state.loadingSubscriptions, [job.id]: false }
      });
    }
  };

  render() {
    const {
      jobs,
      loading,
      activeTab,
      searchQuery,
      currentPage,
      totalPages,
      totalCount,
      showAddModal,
      showEditModal,
      showDeleteModal,
      showShareModal,
      newJobName,
      editJobName,
      selectedJob,
      currentShares,
      shareableUsers,
      loadingShares,
      errors,
      actionLoading,
      jobSubscriptions,
      loadingSubscriptions,
    } = this.state;

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">My Jobs</h1>
              <p className="text-slate-600 mt-1">
                {totalCount} {totalCount === 1 ? 'job' : 'jobs'} total
              </p>
            </div>

            <button
              onClick={this.openAddModal}
              className="btn-primary flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Job</span>
            </button>
          </div>

          {/* Tabs and Search */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
            <div className="p-4 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                {/* Tabs */}
                <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => this.handleTabChange('active')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      activeTab === 'active'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Active Jobs
                  </button>
                  <button
                    onClick={() => this.handleTabChange('archived')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      activeTab === 'archived'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Archived
                  </button>
                </div>

                {/* Search */}
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChange={this.handleSearch}
                    className="w-full px-4 py-2 pl-10 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Jobs Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="large" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Job Name
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition"
                          onClick={() => this.handleSortChange('created_at')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Created</span>
                            {this.getSortIcon('created_at')}
                          </div>
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition"
                          onClick={() => this.handleSortChange('updated_at')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Last Updated</span>
                            {this.getSortIcon('updated_at')}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {jobs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                              </svg>
                              <p className="text-slate-600">
                                {searchQuery ? 'No jobs found matching your search' : `No ${activeTab} jobs`}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        jobs.map((job) => (
                          <tr key={job.id} className="hover:bg-slate-50 transition">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => window.location.href = `/jobs/${job.id}`}
                                      className="text-sm font-medium text-slate-900 hover:text-teal-600 transition text-left"
                                    >
                                      {job.name}
                                    </button>
                                  </div>
                                  {!job.isOwner && (
                                    <div className="flex items-center space-x-2 mt-0.5">
                                      <p className="text-xs text-slate-500">
                                        Owner: {job.ownerName}
                                      </p>
                                      {job.isShared ? (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                          Shared with you
                                        </span>
                                      ) : this.context.user?.admin ? (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                          Admin access
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {this.formatDate(job.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                              {this.formatDate(job.updatedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-1">
                                {/* Watch/Unwatch button */}
                                <button
                                  onClick={() => this.toggleWatch(job)}
                                  disabled={loadingSubscriptions[job.id]}
                                  className={`relative transition ${loadingSubscriptions[job.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title={jobSubscriptions[job.id]?.subscribed ? 'Unwatch' : 'Watch'}
                                >
                                  {loadingSubscriptions[job.id] ? (
                                    <LoadingSpinner size="small" />
                                  ) : (
                                    <div className={`p-1.5 rounded-full transition ${
                                      jobSubscriptions[job.id]?.subscribed
                                        ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                        : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                                    }`}>
                                      <svg className="w-5 h-5" fill={jobSubscriptions[job.id]?.subscribed ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={jobSubscriptions[job.id]?.subscribed ? 0 : 2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </div>
                                  )}
                                </button>

                                {/* Edit button - only for owners and admins */}
                                {this.canModifyJob(job) && (
                                  <button
                                    onClick={() => this.openEditModal(job)}
                                    className="p-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-full transition"
                                    title="Edit"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                )}

                                {/* Share button - only for owners and admins */}
                                {this.canShareJob(job) && (
                                  <button
                                    onClick={() => this.openShareModal(job)}
                                    className="p-1.5 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-full transition"
                                    title="Share"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                  </button>
                                )}

                                {/* Archive button - only for owners and admins */}
                                {this.canModifyJob(job) && (
                                  <button
                                    onClick={() => this.handleToggleArchive(job)}
                                    className="p-1.5 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded-full transition"
                                    title={job.archived ? 'Unarchive' : 'Archive'}
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => this.handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => this.handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Add Job Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Add New Job</h2>

              {errors.general && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.general}
                </div>
              )}

              <form onSubmit={this.handleAddJob} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Job Name
                  </label>
                  <input
                    type="text"
                    value={newJobName}
                    onChange={(e) => this.setState({ newJobName: e.target.value, errors: {} })}
                    className={`input-field ${errors.newJobName ? 'border-red-500' : ''}`}
                    placeholder="Enter job name"
                    autoFocus
                  />
                  {errors.newJobName && (
                    <p className="error-text">{errors.newJobName}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={this.closeAddModal}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <LoadingSpinner size="small" /> : 'Create Job'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Job Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-slate-900">Edit Job</h2>
                <button
                  onClick={() => this.openDeleteModal(this.state.editJob)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                >
                  Delete Job
                </button>
              </div>

              {errors.general && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.general}
                </div>
              )}

              <form onSubmit={this.handleEditJob} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Job Name
                  </label>
                  <input
                    type="text"
                    value={editJobName}
                    onChange={(e) => this.setState({ editJobName: e.target.value, errors: {} })}
                    className={`input-field ${errors.editJobName ? 'border-red-500' : ''}`}
                    autoFocus
                  />
                  {errors.editJobName && (
                    <p className="error-text">{errors.editJobName}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={this.closeEditModal}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <LoadingSpinner size="small" /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Job Modal */}
        {showDeleteModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">Delete Job?</h2>
              <p className="text-slate-600 text-center mb-6">
                Are you sure you want to delete <strong>{selectedJob.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={this.closeDeleteModal}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleDeleteJob}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
                  disabled={actionLoading}
                >
                  {actionLoading ? <LoadingSpinner size="small" /> : 'Delete Job'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Job Modal */}
        {showShareModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Share Job</h2>
              <p className="text-slate-600 mb-6">{selectedJob.name}</p>

              {errors.share && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.share}
                </div>
              )}

              {loadingShares ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="large" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Shares */}
                  {currentShares.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Shared With</h3>
                      <div className="space-y-2">
                        {currentShares.map((share) => (
                          <div key={share.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-slate-900">{share.userName}</p>
                                {/* ✅ Show admin badge */}
                                {share.isAdmin && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{share.userEmail}</p>
                            </div>
                            <button
                              onClick={() => this.handleUnshare(share.userId)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Users to Share With */}
                  {shareableUsers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Share With</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {shareableUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-slate-900">{user.name}</p>
                                {/* ✅ Show admin badge */}
                                {user.admin && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{user.email}</p>
                              {/* ✅ Show note for admins */}
                              {user.admin && (
                                <p className="text-xs text-slate-400 mt-0.5 italic">
                                  Already has admin access to all jobs
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => this.handleShareWithUser(user.id)}
                              className="px-3 py-1 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
                            >
                              Share
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {shareableUsers.length === 0 && currentShares.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No users available to share with</p>
                  )}
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={this.closeShareModal}
                  className="px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default Jobs;